/**
 * Charts of the pay calculator (T1039), ported from docs/design/html/flows/web-landing-tool-pay.mjs.
 * Inline SVG without a viewBox: x and width are percentages of the real width, so patterns and 13 px labels are never
 * stretched. Every chart is aria-hidden and sits next to its text equivalent (a table or a sentence). Segments differ
 * by pattern and a card-coloured gap, never by colour alone. Updates fade in on opacity/transform only
 * (`pc-fade-in` in apps/core/styles/index.css), off under prefers-reduced-motion.
 */
import type { Money } from "@stb/core/money"
import type { Bracket, PeriodBreakdown } from "@stb/jurisdiction-au/income-tax"
import type { LucideIcon } from "lucide-react"

import { useLocale } from "@/core/i18n/locale-context"

import { CHART_COPY_ZH } from "../../content/zh/pay-calculator-copy"
import type { BracketPosition, SplitKey, TaxCut } from "./pay-calculator-model"

/** Text inside the charts (the charts are aria-hidden; their text equivalents are in pay-calculator.tsx). */
const CHART_COPY = {
  en: { you: (taxable: string) => `You · ${taxable}`, noLevy: "No levy" },
  zh: CHART_COPY_ZH,
}
const useChartCopy = () => CHART_COPY[useLocale()]

export type ChipTone = "primary" | "success" | "warning" | "violet" | "muted"
const CHIP_TONES: Record<ChipTone, string> = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/15 text-success",
  warning: "bg-warning-soft text-warning",
  violet: "bg-chart-5/15 text-chart-5",
  muted: "bg-muted text-muted-foreground",
}

/** Small decorative icon tile (the text beside it carries the meaning). */
export const IconChip = ({ icon: Icon, tone }: { icon: LucideIcon; tone: ChipTone }) => (
  <span
    aria-hidden="true"
    data-icon-chip=""
    className={`grid size-7 shrink-0 place-items-center rounded-md ${CHIP_TONES[tone]}`}
  >
    <Icon className="size-4" />
  </span>
)

export type FillKey = SplitKey | "super" | "save"

/** `url(#…)` needs a plain id; useId() output contains characters that must not appear there. */
export const patternId = (base: string, key: FillKey) =>
  `${base.replace(/[^a-zA-Z0-9_-]/g, "")}-${key}`

/** One pattern per part, defined once per calculator. */
export const ChartPatterns = ({ base }: { base: string }) => (
  <svg aria-hidden="true" className="absolute size-0 overflow-hidden">
    <defs>
      <pattern id={patternId(base, "takeHome")} width="6" height="6" patternUnits="userSpaceOnUse">
        <rect width="6" height="6" className="fill-success" />
      </pattern>
      <pattern
        id={patternId(base, "tax")}
        width="6"
        height="6"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width="6" height="6" className="fill-primary" />
        <rect width="1.6" height="6" className="fill-card opacity-60" />
      </pattern>
      <pattern id={patternId(base, "medicare")} width="4" height="4" patternUnits="userSpaceOnUse">
        <rect width="4" height="4" className="fill-chart-4" />
        <circle cx="2" cy="2" r="0.9" className="fill-card opacity-75" />
      </pattern>
      <pattern
        id={patternId(base, "help")}
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width="5" height="5" className="fill-chart-5" />
        <rect width="1" height="5" className="fill-card opacity-70" />
        <rect width="5" height="1" className="fill-card opacity-70" />
      </pattern>
      <pattern id={patternId(base, "super")} width="5" height="5" patternUnits="userSpaceOnUse">
        <rect width="2" height="5" className="fill-muted-foreground opacity-35" />
      </pattern>
      <pattern
        id={patternId(base, "save")}
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width="2" height="5" className="fill-success" />
      </pattern>
    </defs>
  </svg>
)

/** Legend key that matches a bar segment. */
export const FillKeySwatch = ({ base, fill }: { base: string; fill: FillKey }) => (
  <svg aria-hidden="true" width="18" height="12" className="shrink-0 overflow-visible">
    <rect
      width="18"
      height="12"
      rx="3"
      fill={`url(#${patternId(base, fill)})`}
      className={
        fill === "super" ? "stroke-muted-foreground" : fill === "save" ? "stroke-success" : ""
      }
      strokeWidth={fill === "super" || fill === "save" ? 1.5 : 0}
    />
  </svg>
)

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0)
/** SVG percentage attribute; display geometry only, never an amount. */
const p = (n: number) => `${Math.max(0, Math.min(100, n)).toFixed(3)}%`

const fade = (animate: boolean) =>
  animate ? "animate-[pc-fade-in_200ms_cubic-bezier(.2,.8,.2,1)] motion-reduce:animate-none" : ""

/** (a) Pay before tax split into take-home, tax, Medicare and HELP, with employer super drawn on top (dashed). */
export function PaySplitBar({
  base,
  period,
  animate,
}: {
  base: string
  period: PeriodBreakdown | undefined
  animate: boolean
}) {
  const parts: [FillKey, Money | undefined][] = [
    ["takeHome", period?.takeHome],
    ["tax", period?.incomeTaxAfterLito],
    ["medicare", period?.medicareLevy],
    ["help", period?.studyLoanRepayment],
  ]
  const gross = period?.gross.amount ?? 0
  const total = gross + (period?.employerSuper.amount ?? 0)
  let x = 0
  const segments = parts.flatMap(([key, value]) => {
    const w = pct(value?.amount ?? 0, total)
    const seg = { key, x, w }
    x += w
    return w > 0 ? [seg] : []
  })
  const superX = pct(gross, total)
  return (
    <svg
      aria-hidden="true"
      data-testid="pay-split-bar"
      width="100%"
      height="20"
      className="block h-5 w-full overflow-visible"
    >
      <rect width="100%" height="20" rx="4" className="fill-muted" />
      <g key={`${gross}-${total}`} className={`${fade(animate)} origin-left`}>
        {segments.map((s) => (
          <rect
            key={s.key}
            x={p(s.x)}
            width={p(s.w)}
            height="20"
            rx="3"
            fill={`url(#${patternId(base, s.key)})`}
            className="stroke-card"
            strokeWidth="2"
          />
        ))}
        {total > gross && (
          <rect
            x={p(superX)}
            width={p(100 - superX)}
            height="20"
            rx="4"
            fill={`url(#${patternId(base, "super")})`}
            className="stroke-muted-foreground"
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
        )}
      </g>
    </svg>
  )
}

const LADDER_H = 140
const BASE_Y = 112

/** (b) Bracket ladder: step height = rate, width = income range, a "You" marker at the taxable income. */
export function BracketLadder({
  brackets,
  taxable,
  position,
  animate,
}: {
  brackets: readonly Bracket[]
  taxable: number | undefined
  position: BracketPosition | undefined
  animate: boolean
}) {
  const copy = useChartCopy()
  const last = brackets.at(-1)?.from ?? 0
  const upper = Math.max(250_000, Math.min((taxable ?? 0) * 1.1, 400_000), last * 1.25)
  const maxRate = Math.max(...brackets.map((b) => b.rateBp), 1)
  const marker = taxable === undefined ? undefined : Math.min(pct(taxable, upper), 100)
  const flip = (marker ?? 0) > 62
  return (
    <svg
      aria-hidden="true"
      data-testid="bracket-ladder"
      width="100%"
      height={LADDER_H}
      className="block h-[140px] w-full overflow-hidden"
    >
      {brackets.map((b, k) => {
        const from = pct(b.from, upper)
        const to = pct(brackets[k + 1]?.from ?? upper, upper)
        const h = Math.max(3, (b.rateBp / maxRate) * 78)
        const y = BASE_Y - h
        const you = position?.index === k
        const below = position !== undefined && k < position.index
        const fillTo = you && marker !== undefined ? Math.min(marker, to) : from
        return (
          <g key={b.from}>
            <rect
              x={p(from)}
              width={p(to - from)}
              y={y}
              height={h}
              rx="3"
              className={`${below ? "fill-primary/30" : "fill-card"} stroke-primary/50`}
              strokeWidth="1.5"
            />
            {you && fillTo > from && (
              <rect
                key={`${taxable}`}
                x={p(from)}
                width={p(fillTo - from)}
                y={y}
                height={h}
                rx="3"
                className={`fill-primary ${fade(animate)}`}
              />
            )}
            <text
              x={p(from)}
              dx="4"
              y={y - 6}
              className={`text-[13px] font-semibold ${you ? "fill-primary" : "fill-foreground"}`}
            >
              {b.rateBp / 100}%
            </text>
            {k > 0 && (
              <text
                x={p(from)}
                y={BASE_Y + 18}
                textAnchor="middle"
                className="fill-muted-foreground text-[13px]"
              >
                {(b.from / 1000).toLocaleString("en-AU", { maximumFractionDigits: 1 })}
              </text>
            )}
          </g>
        )
      })}
      <line x1="0" x2="100%" y1={BASE_Y} y2={BASE_Y} className="stroke-border" />
      {marker !== undefined && taxable !== undefined && (
        <g key={`m-${taxable}`} className={fade(animate)}>
          <line
            x1={p(marker)}
            x2={p(marker)}
            y1="18"
            y2={BASE_Y}
            className="stroke-foreground"
            strokeWidth="2"
          />
          <text
            x={p(marker)}
            dx={flip ? -6 : 6}
            y="13"
            textAnchor={flip ? "end" : "start"}
            className="fill-foreground text-[13px] font-semibold"
          >
            {copy.you(`A$${taxable.toLocaleString("en-AU")}`)}
          </text>
        </g>
      )}
    </svg>
  )
}

/** (c) Tax and Medicare in the two years on one scale (not truncated), the saving hatched on the newer bar. */
export function TaxCutBars({
  base,
  cut,
  previousLabel,
  currentLabel,
  format,
  animate,
}: {
  base: string
  cut: TaxCut
  previousLabel: string
  currentLabel: string
  format: (value: Money) => string
  animate: boolean
}) {
  const max = Math.max(cut.previous.amount, cut.current.amount, 1)
  const save = Math.max(0, cut.saving.amount)
  const rows: [string, Money, boolean][] = [
    [previousLabel, cut.previous, false],
    [currentLabel, cut.current, true],
  ]
  return (
    <div aria-hidden="true" className="grid gap-2" data-testid="tax-cut-bars">
      {rows.map(([label, value, isNow]) => (
        <div
          key={label}
          className="grid grid-cols-[4rem_minmax(0,1fr)_5.5rem] items-center gap-2 text-sm whitespace-nowrap"
        >
          <span>{label}</span>
          <svg width="100%" height="16" className="block h-4 w-full">
            <g key={`${value.amount}`} className={fade(animate)}>
              <rect
                width={p(pct(value.amount, max))}
                height="16"
                rx="3"
                fill={`url(#${patternId(base, "tax")})`}
              />
              {isNow && save > 0 && (
                <rect
                  x={p(pct(value.amount, max))}
                  width={p(pct(save, max))}
                  height="16"
                  rx="3"
                  fill={`url(#${patternId(base, "save")})`}
                  className="stroke-success"
                  strokeWidth="1.5"
                />
              )}
            </g>
          </svg>
          <b className="text-right font-semibold tabular-nums">{format(value)}</b>
        </div>
      ))}
    </div>
  )
}

const thousands = (n: number) => (n / 1000).toLocaleString("en-AU", { maximumFractionDigits: 1 })

/** Medicare levy against taxable income for a single person: nil, the 10c shade-in, then the full rate. The line is
 * drawn from the rule table (display only); the "You" figure beside it is the engine's. */
export function MedicareShadeChart({
  lower,
  upper,
  shadeInBp,
  rateBp,
  taxable,
  animate,
}: {
  lower: number
  upper: number
  shadeInBp: number
  rateBp: number
  taxable: number | undefined
  animate: boolean
}) {
  const copy = useChartCopy()
  const xMax = Math.max(upper * 1.6, Math.min((taxable ?? 0) * 1.1, upper * 3))
  const levyAt = (t: number) =>
    t <= lower ? 0 : Math.min(((t - lower) * shadeInBp) / 10_000, (t * rateBp) / 10_000)
  const yMax = levyAt(xMax) || 1
  const y = (t: number) => BASE_Y - (levyAt(t) / yMax) * 78
  const marker = taxable === undefined ? undefined : Math.min(pct(taxable, xMax), 100)
  const flip = (marker ?? 0) > 62
  const stroke = "stroke-primary"
  return (
    <svg
      aria-hidden="true"
      data-testid="medicare-shade-chart"
      width="100%"
      height={LADDER_H}
      className="block h-[140px] w-full overflow-hidden"
    >
      <line x1="0" x2="100%" y1={BASE_Y} y2={BASE_Y} className="stroke-border" />
      <line
        x1="0"
        x2={p(pct(lower, xMax))}
        y1={y(0)}
        y2={y(lower)}
        className={stroke}
        strokeWidth="2.5"
      />
      <line
        x1={p(pct(lower, xMax))}
        x2={p(pct(upper, xMax))}
        y1={y(lower)}
        y2={y(upper)}
        className={stroke}
        strokeWidth="2.5"
        strokeDasharray="6 4"
      />
      <line
        x1={p(pct(upper, xMax))}
        x2="100%"
        y1={y(upper)}
        y2={y(xMax)}
        className={stroke}
        strokeWidth="2.5"
      />
      {[lower, upper].map((t) => (
        <text
          key={t}
          x={p(pct(t, xMax))}
          y={BASE_Y + 18}
          textAnchor="middle"
          className="fill-muted-foreground text-[13px]"
        >
          {thousands(t)}
        </text>
      ))}
      <text x="4" y={y(0) - 8} className="fill-muted-foreground text-[13px]">
        {copy.noLevy}
      </text>
      {marker !== undefined && taxable !== undefined && (
        <g key={`m-${taxable}`} className={fade(animate)}>
          <line
            x1={p(marker)}
            x2={p(marker)}
            y1="18"
            y2={BASE_Y}
            className="stroke-foreground"
            strokeWidth="2"
          />
          <circle cx={p(marker)} cy={y(Math.min(taxable, xMax))} r="5" className="fill-primary" />
          <text
            x={p(marker)}
            dx={flip ? -6 : 6}
            y="13"
            textAnchor={flip ? "end" : "start"}
            className="fill-foreground text-[13px] font-semibold"
          >
            {copy.you(`A$${taxable.toLocaleString("en-AU")}`)}
          </text>
        </g>
      )}
    </svg>
  )
}
